<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ArrowLeft, ArrowRight, ChevronRight } from 'lucide-vue-next';
import { computed, nextTick, onMounted, watch } from 'vue';
import HelpSidebar from '@/components/help/HelpSidebar.vue';
import { MarketingFooter, MarketingHeader } from '@/components/home';
import MarketingMeta from '@/components/seo/MarketingMeta.vue';
import type { HelpArticle, HelpArticleSummary, HelpBreadcrumb, HelpIndex } from '@/types/help';

interface Props {
    article: HelpArticle;
    breadcrumbs: HelpBreadcrumb[];
    prev: HelpArticleSummary | null;
    next: HelpArticleSummary | null;
    index: HelpIndex;
}

const props = defineProps<Props>();

const currentPath = computed(() => (props.article.section ? `${props.article.section}/${props.article.slug}` : props.article.slug));

/**
 * Scroll to the heading the URL fragment points at. The browser only does
 * this automatically on a full page load — Inertia navigations swap the DOM
 * without triggering the default fragment-scroll, so we handle it here.
 */
const scrollToHash = () => {
    if (typeof window === 'undefined') {
        return;
    }

    const hash = window.location.hash.replace(/^#/, '');

    if (!hash) {
        return;
    }

    void nextTick(() => {
        const target = document.getElementById(hash);

        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    });
};

onMounted(scrollToHash);

// When clicking between articles (e.g. sidebar search → article A → article B),
// the `article` prop changes but the component isn't re-mounted. Re-run the
// scroll whenever a new article's HTML is swapped in.
watch(() => props.article.slug, scrollToHash);
</script>

<template>
    <div class="bg-background">
        <MarketingMeta :title="article.title" :description="article.description" :path="`/help/${currentPath}`" />

        <MarketingHeader />

        <main class="pt-28 pb-20 sm:pt-32 sm:pb-28 lg:pt-40 lg:pb-32">
            <div class="container mx-auto px-6 lg:px-8">
                <div class="mx-auto max-w-6xl">
                    <div class="grid gap-10 lg:grid-cols-[260px_minmax(0,1fr)]">
                        <HelpSidebar :index="index" :current-path="currentPath" />

                        <article>
                            <!-- Breadcrumbs -->
                            <nav class="text-muted-foreground mb-6 flex flex-wrap items-center gap-1 text-sm">
                                <template v-for="(crumb, i) in breadcrumbs" :key="i">
                                    <ChevronRight v-if="i > 0" class="h-3.5 w-3.5" />
                                    <Link v-if="crumb.url" :href="crumb.url" class="hover:text-primary">
                                        {{ crumb.label }}
                                    </Link>
                                    <span v-else class="text-foreground">{{ crumb.label }}</span>
                                </template>
                            </nav>

                            <!-- Article content -->
                            <div
                                class="prose prose-slate dark:prose-invert prose-headings:scroll-mt-28 prose-headings:font-display prose-headings:font-normal prose-a:text-primary prose-a:no-underline prose-a:hover:underline prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:text-sm prose-code:before:content-[''] prose-code:after:content-[''] max-w-none"
                                v-dompurify-html="article.html"
                            />

                            <!-- Prev / Next navigation -->
                            <div v-if="prev || next" class="border-border mt-12 grid gap-4 border-t pt-8 sm:grid-cols-2">
                                <Link
                                    v-if="prev"
                                    :href="prev.url"
                                    class="group border-border bg-card hover:border-primary flex flex-col gap-1 rounded-sm border p-4 transition"
                                >
                                    <span class="text-muted-foreground flex items-center gap-1 text-xs">
                                        <ArrowLeft class="h-3.5 w-3.5" /> Previous
                                    </span>
                                    <span class="text-foreground group-hover:text-primary font-medium">{{ prev.title }}</span>
                                </Link>
                                <span v-else />

                                <Link
                                    v-if="next"
                                    :href="next.url"
                                    class="group border-border bg-card hover:border-primary flex flex-col gap-1 rounded-sm border p-4 text-right transition sm:col-start-2"
                                >
                                    <span class="text-muted-foreground flex items-center justify-end gap-1 text-xs">
                                        Next <ArrowRight class="h-3.5 w-3.5" />
                                    </span>
                                    <span class="text-foreground group-hover:text-primary font-medium">{{ next.title }}</span>
                                </Link>
                            </div>
                        </article>
                    </div>
                </div>
            </div>
        </main>

        <MarketingFooter />
    </div>
</template>
